import type { GeoScoreSummary } from '@lensora/shared'
import { cn } from '@lensora/ui'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { FaviconImg } from './FaviconImg'

export interface LeaderboardEntry {
  rank: number
  competitor_id: string | null
  name: string
  domain: string
  favicon_url: string
  mention_rate: number
  avg_position: number | null
  sentiment_score: number
  overall_score: number
  overall_delta: number | null
  mention_rate_delta: number | null
  sentiment_delta: number | null
  position_delta: number | null
  scan_count: number
  is_own_brand: boolean
  is_brand_placeholder: boolean
}

const SCORE_TIER = {
  success: { text: 'text-feedback-success', stroke: 'stroke-feedback-success' },
  warning: { text: 'text-feedback-warning', stroke: 'stroke-feedback-warning' },
  error: { text: 'text-feedback-error', stroke: 'stroke-feedback-error' },
} as const

function scoreTier(score: number): keyof typeof SCORE_TIER {
  if (score >= 70) return 'success'
  if (score >= 40) return 'warning'
  return 'error'
}

const ARC_R = 52
const ARC_CX = 70
const ARC_CY = 60
const ARC_STROKE_WIDTH = 9
const ARC_PATH = `M ${ARC_CX - ARC_R} ${ARC_CY} A ${ARC_R} ${ARC_R} 0 0 1 ${ARC_CX + ARC_R} ${ARC_CY}`
const ARC_CIRCUMFERENCE = Math.PI * ARC_R

function ScoreArc({ score }: { score: number }) {
  const tier = SCORE_TIER[scoreTier(score)]
  const filled = (score / 100) * ARC_CIRCUMFERENCE

  return (
    <div className="relative h-[76px] w-[140px] shrink-0">
      <svg width="140" height="76" viewBox="0 0 140 76">
        <path
          d={ARC_PATH}
          fill="none"
          className="stroke-border"
          strokeWidth={ARC_STROKE_WIDTH}
          strokeLinecap="round"
        />
        <path
          d={ARC_PATH}
          fill="none"
          className={tier.stroke}
          strokeWidth={ARC_STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${ARC_CIRCUMFERENCE}`}
        />
      </svg>
      <div className="absolute inset-x-0 bottom-0 text-center">
        <span className={cn('text-3xl font-extrabold leading-none', tier.text)}>{score}</span>
        <span className="block text-xs text-text-muted">/100 Visibility Score</span>
      </div>
    </div>
  )
}

function LeaderboardRow({ entry, isLast }: { entry: LeaderboardEntry; isLast: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 py-1.5',
        entry.is_own_brand ? 'rounded-md bg-brand-accent/5 px-1' : 'px-0',
        !isLast && !entry.is_own_brand && 'border-b border-border'
      )}
    >
      <span
        className={cn(
          'w-4 shrink-0 text-xs',
          entry.is_own_brand ? 'font-bold text-brand-accent' : 'text-text-muted'
        )}
      >
        {entry.rank}
      </span>
      {entry.is_own_brand ? (
        <div className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-brand-accent text-[7px] font-bold text-text-inverse">
          Y
        </div>
      ) : (
        <FaviconImg domain={entry.domain} name={entry.name} size={14} className="rounded-sm" />
      )}
      <span
        className={cn(
          'flex-1 truncate text-xs',
          entry.is_own_brand ? 'font-semibold text-brand-accent' : 'text-text'
        )}
      >
        {entry.is_own_brand ? 'You' : entry.name}
      </span>
      <span
        className={cn(
          'shrink-0 text-xs font-semibold',
          entry.is_own_brand ? 'text-brand-accent' : 'text-feedback-success'
        )}
      >
        {entry.mention_rate}%
      </span>
      {entry.overall_delta !== null && entry.overall_delta !== 0 && (
        <span
          className={cn(
            'shrink-0 text-[10px]',
            entry.overall_delta > 0 ? 'text-feedback-success' : 'text-feedback-error'
          )}
        >
          {entry.overall_delta > 0 ? '↑' : '↓'}
          {Math.abs(entry.overall_delta)}
        </span>
      )}
    </div>
  )
}

interface GeoLeaderboardHeroProps {
  // Headline arc — sourced from the Visibility Score engine (visibility_scores.overallScore),
  // not the GEO summary below. Kept as a separate prop so this one number can't drift back
  // to the old live GEO calculation (needs-decision backlog item #32 SCORE-DUAL-MODEL: the
  // dashboard previously showed two different "score" numbers for the same brand).
  score: number
  // Supporting stats (mention rate, avg position, prompt coverage) — these have no equivalent
  // field on visibility_scores, so they still come from the GEO summary. They're descriptive
  // stats, not a competing score, so leaving them on the existing engine doesn't reintroduce
  // the dual-number problem.
  geoData: GeoScoreSummary | null
  leaderboard: LeaderboardEntry[]
  leaderboardLoading: boolean
}

export function GeoLeaderboardHero({
  score,
  geoData,
  leaderboard,
  leaderboardLoading,
}: GeoLeaderboardHeroProps) {
  const metrics = [
    { label: 'Mention rate', value: `${geoData?.mentionRate ?? 0}%` },
    { label: 'Avg position', value: geoData?.avgPosition != null ? `#${geoData.avgPosition}` : '—' },
    { label: 'Prompt coverage', value: `${geoData?.promptCoverage ?? 0}%` },
  ]

  return (
    <div className="box-border flex h-full flex-col overflow-hidden rounded-lg border border-border bg-surface p-5">
      <div className="flex shrink-0 items-end gap-4">
        <ScoreArc score={score} />
        <div className="flex gap-4 pb-1">
          {metrics.map((m) => (
            <div key={m.label}>
              <p className="mb-0.5 text-[10px] uppercase tracking-wide text-text-muted">{m.label}</p>
              <p className="text-lg font-bold text-text">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="my-3 h-px shrink-0 bg-border" />

      <div className="mb-2 flex shrink-0 items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
          AI visibility rankings
        </p>
        <Link to="/dashboard/competitors" className="text-xs text-brand-accent hover:underline">
          Full leaderboard →
        </Link>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-2">
        {leaderboardLoading ? (
          <div className="space-y-1.5">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-6 animate-pulse rounded bg-surface-overlay" />
            ))}
          </div>
        ) : leaderboard.length === 0 ? (
          <p className="text-xs text-text-muted">Track competitors to unlock rankings</p>
        ) : (
          leaderboard.map((entry, i) => (
            <LeaderboardRow key={entry.rank} entry={entry} isLast={i === leaderboard.length - 1} />
          ))
        )}
      </div>
    </div>
  )
}
